import { computed, effect, inject, Injectable, Signal, signal, untracked, WritableSignal } from '@angular/core';
import { SsrCookieService } from 'ngx-cookie-service-ssr';
import { getMessageFromError } from '../../models/error';
import { IChainStoreListItem, IChainstoreListRender, IMapLocation, IMyChainstore } from '../../models';
import { ComponentProvider, MyChainstoreProvider } from '../../providers';
import { ConfigService } from '../config/config.service';
import { ToastService } from '../toast.service';

/** Page size used when loading the selectable stores — high enough to cover every realistic store count */
const STORE_PAGE_SIZE = 999;
/** Number of stores from which the selection panel offers a search field */
const SEARCH_THRESHOLD = 8;
/** Cookie the backend reads the selected store from; the auth interceptor forwards it as a request header */
const CHAINSTORE_COOKIE = 'gs.ocs.mychainstore';
/** Lifetime of that cookie in days, matching what the backend sets */
const CHAINSTORE_COOKIE_DAYS = 365;

@Injectable({
  providedIn: 'root'
})
export class MyChainstoreService {
  // Services
  private readonly componentProvider = inject(ComponentProvider);
  private readonly configService = inject(ConfigService);
  private readonly cookieService = inject(SsrCookieService);
  private readonly myChainstoreProvider = inject(MyChainstoreProvider);
  private readonly toastService = inject(ToastService);

  // Properties & Signals
  /** Whether the store-selection panel is open */
  public readonly panelOpen: WritableSignal<boolean> = signal(false);
  /** The store the visitor selected; not set while none is chosen */
  public readonly current: WritableSignal<IMyChainstore | undefined> = signal(undefined);
  /** All selectable stores; empty until the panel is opened for the first time */
  public readonly stores: WritableSignal<IChainStoreListItem[]> = signal([]);
  /** Whether the store list is currently being loaded */
  public readonly loading: WritableSignal<boolean> = signal(false);
  /** Search term the visitor typed; matched against name, zip and city */
  public readonly search: WritableSignal<string> = signal('');
  /** Whether the search field is offered — only worthwhile from SEARCH_THRESHOLD stores upwards */
  public readonly showSearch: Signal<boolean> = computed(() => this.stores().length >= SEARCH_THRESHOLD);
  /** The stores matching the current search term, filtered client-side over the loaded list */
  public readonly filteredStores: Signal<IChainStoreListItem[]> = computed(() => {
    const term = this.search().trim().toLowerCase();
    const stores = this.stores();
    if (!term) return stores;
    return stores.filter((store) =>
      [store.name, store.address?.zip, store.address?.city].some((part) => part?.toLowerCase().includes(term))
    );
  });
  /** Map markers for the stores that carry coordinates; empty when none of them does */
  public readonly mapLocations: Signal<IMapLocation[]> = computed(() =>
    this.filteredStores()
      .filter((store) => typeof store.address?.latitude === 'number' && typeof store.address?.longitude === 'number')
      .map((store) => ({
        id: store.iD ?? store.name,
        lat: store.address!.latitude!,
        lng: store.address!.longitude!,
        title: store.name,
        address: this.getAddressText(store),
        country: store.address?.country
      }))
  );
  /** Whether the stores have already been requested, so opening the panel again does not reload them */
  private storesLoaded = false;
  /** Whether the selected store has already been requested */
  private currentLoaded = false;

  constructor() {
    // Loads the selected store once the config says the feature is active — shops without it must not pay
    // for the extra backend roundtrip on every (server-rendered) page
    effect(() => {
      const active = this.configService.config()?.header?.mainbar?.myChainstore?.active;
      if (!active || this.currentLoaded) return;
      untracked(() => this.loadCurrent());
    });
  }

  // Methods
  /** Opens the selection panel and loads the selectable stores on first open. */
  public openPanel(): void {
    this.panelOpen.set(true);
    this.loadStores();
  }

  /** Closes the selection panel. */
  public closePanel(): void {
    this.panelOpen.set(false);
  }

  /**
   * Selects the given store as the visitor's preferred one and closes the panel.
   * @param store The store the visitor picked
   */
  public select(store: IChainStoreListItem): void {
    if (store.iD === undefined) return;

    // Applied right away so the panel can close on click; rolled back when the backend refuses the store
    const previous = this.current();
    // Kept to recognise afterwards whether the auth interceptor stored the backend's own cookie value
    const cookieBefore = this.cookieService.get(CHAINSTORE_COOKIE);
    this.current.set({ chainStoreID: store.iD, name: store.name });
    this.closePanel();

    this.myChainstoreProvider.set(store.iD).subscribe({
      next: (result) => {
        // The backend answers 200 with an error message when the visitor declined cookies
        if (result?.error) {
          this.current.set(previous);
          this.toastService.showToast('', result.error, 'error');
          return;
        }
        if (result?.chainStore) this.current.set(result.chainStore);
        // Fallback for backends whose response header is not exposed via CORS: an unchanged cookie means the
        // interceptor stored nothing. Comparing against the id instead would overwrite a differently encoded
        // backend value, and checking existence alone would skip every selection after the first one.
        if (this.cookieService.get(CHAINSTORE_COOKIE) === cookieBefore) {
          this.cookieService.set(CHAINSTORE_COOKIE, String(store.iD), {
            expires: CHAINSTORE_COOKIE_DAYS,
            path: '/',
            secure: true,
            sameSite: 'Lax'
          });
        }
      },
      error: (error) => {
        this.current.set(previous);
        console.error('[MyChainstore] Selecting the store failed', error);
        this.toastService.showToast('', getMessageFromError(error) ?? '', 'error');
      }
    });
  }

  /**
   * Builds a display-ready, one-line address for a store.
   * @param store The store to read the address from
   * @returns The address text, or an empty string when no address parts are available
   */
  public getAddressText(store: IChainStoreListItem): string {
    const streetPart = [store.address?.street, store.address?.houseNumber].filter(Boolean).join(' ');
    const cityPart = [store.address?.zip, store.address?.city].filter(Boolean).join(' ');
    return [streetPart, cityPart].filter(Boolean).join(', ');
  }

  /** Loads the store the visitor currently has selected. */
  private loadCurrent(): void {
    this.currentLoaded = true;
    this.myChainstoreProvider.getCurrent().subscribe({
      next: (result) => this.current.set(result?.chainStore),
      error: (error) => console.error('[MyChainstore] Loading the selected store failed', error)
    });
  }

  /** Loads the selectable stores once and keeps them for the session. */
  private loadStores(): void {
    if (this.storesLoaded) return;
    this.storesLoaded = true;
    this.loading.set(true);
    this.componentProvider
      .get<IChainstoreListRender, { code: { PageSize: number } }>('ChainstoreList', { code: { PageSize: STORE_PAGE_SIZE } })
      .subscribe({
        next: (result) => {
          this.stores.set(result?.items?.data ?? []);
          this.loading.set(false);
        },
        error: (error) => {
          console.error('[MyChainstore] Loading the stores failed', error);
          this.storesLoaded = false;
          this.loading.set(false);
        }
      });
  }
}
